import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const Section = ({ n, title, children }) => (
  <section className="mb-10" data-testid={`terms-section-${n}`}>
    <h2 className="text-sm uppercase tracking-[0.2em] text-zinc-500 mb-3">
      {String(n).padStart(2, "0")} — {title}
    </h2>
    <div className="text-zinc-300 text-sm leading-relaxed space-y-3">{children}</div>
  </section>
);

const Terms = () => {
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-zinc-100" data-testid="terms-page">
      <div className="max-w-3xl mx-auto px-6 py-16">
        <Link
          to="/"
          data-testid="terms-back-link"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-zinc-500 hover:text-white transition-colors mb-12"
        >
          <ArrowLeft className="w-3 h-3" /> Back
        </Link>

        <header className="mb-14">
          <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-3">Legal</div>
          <h1 className="text-4xl sm:text-5xl font-medium tracking-tight">Terms of Service</h1>
          <div className="mt-4 text-xs text-zinc-500 font-mono">Last updated: February 14, 2026</div>
        </header>

        <Section n={1} title="Who we are">
          <p>
            Quill (&quot;Quill&quot;, &quot;we&quot;, &quot;us&quot;) is a non-custodial invoicing tool
            that lets creators bill their clients via direct crypto wallet payments. By accessing or
            using Quill (the &quot;Service&quot;) you agree to these Terms of Service (the
            &quot;Terms&quot;). If you do not agree, do not use the Service.
          </p>
        </Section>

        <Section n={2} title="What Quill is — and is not">
          <p>
            Quill generates invoices, tracks on-chain payments to wallet addresses you provide, and
            sends optional email notifications. <span className="text-white">Quill never holds,
            custodies, transmits, or moves your funds.</span> All crypto payments flow directly
            from your client&apos;s wallet to a wallet address you control — Quill never routes
            funds through its own accounts.
          </p>
          <p>
            Quill is not a bank, money transmitter, broker-dealer, exchange, custodian, payment
            processor, or financial advisor. We do not provide accounting, legal, or tax advice.
          </p>
        </Section>

        <Section n={3} title="Your wallet & your responsibility">
          <p>
            You are solely responsible for: (i) the accuracy of any wallet address you enter,
            (ii) the security of your private keys and seed phrases, (iii) all transactions
            originated to or from your wallets, and (iv) compliance with the laws of your
            jurisdiction. Crypto transactions are irreversible. Quill cannot recover funds sent to
            an incorrect address, lost wallets, or compromised keys.
          </p>
        </Section>

        <Section n={4} title="Eligibility & accounts">
          <p>
            You must be at least 18 years old and legally able to enter into a contract. You agree
            to provide accurate information, keep your account credentials secure, and not share
            your account. You are responsible for all activity under your account.
          </p>
        </Section>

        <Section n={5} title="Subscriptions & billing">
          <p>
            Paid tiers (Pro, Team, Studio) are billed on a monthly or annual basis as{" "}
            <span className="text-white">on-chain payments in ETH or BTC</span> sent directly to
            Quill&apos;s platform wallet. There is no card processor for subscriptions. Plans
            unlock strictly after on-chain confirmation. Subscriptions do not auto-renew — you
            will receive a renewal notice before each period ends and can choose to send the next
            on-chain payment or let the subscription lapse. Refunds are governed by our{" "}
            <Link to="/refunds" className="text-white underline underline-offset-4 decoration-zinc-700 hover:decoration-white">Refund Policy</Link>.
          </p>
          <p>
            Free tier limits (e.g. 3 invoices / month) may change with notice. You are responsible
            for reporting and remitting any sales tax, VAT, GST, or income tax that may apply to
            your use of Quill in your jurisdiction.
          </p>
        </Section>

        <Section n={6} title="Acceptable use">
          <p>You agree not to use the Service to:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Violate any law, regulation, or third-party right.</li>
            <li>Engage in money laundering, terrorism financing, fraud, or sanctions evasion.</li>
            <li>Invoice for illegal goods or services.</li>
            <li>Reverse engineer, scrape, or abuse the Service or its APIs.</li>
            <li>Send spam or harass clients via Quill&apos;s email features.</li>
          </ul>
          <p>
            We may suspend or terminate accounts that violate these Terms, at our sole discretion,
            with or without notice.
          </p>
        </Section>

        <Section n={7} title="Third-party services">
          <p>
            Quill relies on third-party services including block explorers (mempool.space,
            Etherscan), email delivery (Resend), and authentication (Google). Their
            availability, accuracy, and terms are outside our control. Quill is not liable for
            any third-party outage or error.
          </p>
        </Section>

        <Section n={8} title="No warranty">
          <p>
            The Service is provided &quot;as is&quot; and &quot;as available&quot;, without
            warranties of any kind, express or implied, including merchantability, fitness for a
            particular purpose, and non-infringement. We do not warrant that the Service will be
            uninterrupted, error-free, or that payment confirmations will be timely or accurate.
          </p>
        </Section>

        <Section n={9} title="Limitation of liability">
          <p>
            To the maximum extent permitted by law, Quill and its affiliates will not be liable
            for any indirect, incidental, consequential, special, or punitive damages, or any loss
            of profits, revenue, data, or crypto-asset value. Our total aggregate liability for
            any claim arising out of or relating to the Service will not exceed the greater of
            (a) the amount you paid Quill in the 12 months preceding the claim, or (b) USD $50.
          </p>
        </Section>

        <Section n={10} title="Indemnity">
          <p>
            You agree to indemnify and hold Quill harmless from any claim, loss, or expense
            (including reasonable legal fees) arising out of your use of the Service, your content,
            your invoices, or your violation of these Terms or any law.
          </p>
        </Section>

        <Section n={11} title="Changes to the Service or Terms">
          <p>
            We may modify the Service or these Terms at any time. Material changes will be
            communicated via the Service or by email. Continued use after changes constitutes
            acceptance.
          </p>
        </Section>

        <Section n={12} title="Termination">
          <p>
            You may stop using the Service at any time. We may suspend or terminate your access at
            any time, with or without cause. Sections that by their nature should survive
            termination (including liability limits, indemnity, and disclaimers) will survive.
          </p>
        </Section>

        <Section n={13} title="Governing law">
          <p>
            These Terms are governed by the laws of the State of Delaware, USA, without regard to
            its conflict of laws rules. Disputes will be resolved exclusively in the state and
            federal courts located in Delaware, and you consent to personal jurisdiction there.
          </p>
        </Section>

        <Section n={14} title="Contact">
          <p>
            Questions about these Terms? Email{" "}
            <a
              href="mailto:legal@quillpayed.com"
              data-testid="terms-contact-email"
              className="text-white underline underline-offset-4 decoration-zinc-700 hover:decoration-white"
            >
              legal@quillpayed.com
            </a>
            .
          </p>
        </Section>

        <div className="mt-16 pt-8 border-t border-zinc-900 flex items-center justify-between text-xs text-zinc-500">
          <Link to="/privacy" data-testid="terms-to-privacy-link" className="hover:text-white transition-colors">
            Privacy Policy →
          </Link>
          <div className="font-mono">© 2026 Quill</div>
        </div>
      </div>
    </div>
  );
};

export default Terms;
